import {
  DragEvent as ReactDragEvent,
  ReactNode,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Download,
  // Aliased: the DOM `File` type is used throughout the import handlers.
  File as FileIcon,
  Info,
  Lock,
  Upload,
} from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { AlertStack } from "../../components/Alert/AlertStack";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
} from "../../components/Card/Card";
import { Checkbox } from "../../components/Checkbox/Checkbox";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Popover } from "../../components/Popover/Popover";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { useToast } from "../../components/Toast/toastContext";
import { ApiError, describeError, errorProblems } from "../../lib/errors";
import { useAuth } from "../auth/useAuth";
import { getBotSettings, listPhrases } from "../bots/api";
import { botSettingsQueryKey } from "../bots/useBotSettingsDraft";
import { BOTS_SECTIONS } from "../bots/settings/sectionMeta";
import { lbKeys, listLeaderboards, listRewards } from "../leaderboards/api";
import type { Leaderboard, LeaderboardStatus } from "../leaderboards/types";
import { fetchActivity } from "../projects/ActivityLog/api";
import { numericDetail } from "../projects/ActivityLog/types";
import type { ActivityItem } from "../projects/ActivityLog/types";
import { projectQueryKey, useProject } from "../projects/useProject";
import {
  downloadSnapshot,
  getGuildTemplateCounts,
  getRosterCounts,
  planSnapshot,
  snapshotFilename,
} from "./api";
import { ImportReview } from "./ImportReview";
import {
  LEGACY_SNAPSHOT_SCHEMA,
  PRE_RHYTHM_SNAPSHOT_SCHEMA,
  SNAPSHOT_SCHEMA,
  type Snapshot,
  type SnapshotPlan,
  type SnapshotSections,
} from "./types";
import styles from "./TransferPage.module.css";

/**
 * Project · Transfer — export a project's settings to one snapshot file and
 * import such a file back after reviewing a server-computed plan.
 *
 * Mockups (binding visual contract):
 *   .codelane/artifacts/t-27/transfer-export.html      — this file (L311-L542)
 *   .codelane/artifacts/t-27/transfer-import-diff.html — ImportReview.tsx
 *
 * This module owns the page shell (header, breadcrumb, header action) in both
 * states and the whole export screen. Once a dropped file has been turned into
 * a plan the body is handed to `ImportReview`; the header switches with it
 * (spec §8.3), because `PageHeader` belongs to the shell.
 */

/**
 * The seven sections of `games.config`, in the order the config diff lists them
 * (`sectionOrder` in internal/admin/snapshot/configdiff.go) — what "7 sections"
 * counts and what the config row's sub-line spells out.
 *
 * Seven and not eight. The eighth used to be the bot simulation, which was a
 * key of this blob and travelled inside this row's tick; it has a table, a row
 * and a checkbox of its own now, so a list that still counted it would promise
 * a section this file does not carry here.
 *
 * `games.config` travels as one opaque blob (spec §2.1), so this is a label for
 * the human rather than a structure anything keys off.
 */
const CONFIG_SECTIONS = [
  "Chat & profanity",
  "Guild defaults",
  "Help requests",
  "Rate limits",
  "Authentication",
  "Profile",
  "Guild assets",
];

/**
 * What the Bot settings row promises, spelled out: the ten cards of the
 * Settings tab, in the tab's own order, read straight off the list that draws
 * them. One source, so the row and the page cannot disagree about how many
 * there are or what they are called.
 *
 * The two keys no card owns — the source switch and the name pools, both on the
 * Source tab — are named in the sentence after it rather than counted here: the
 * badge says «10 sections» because the tab has ten cards, and a badge that said
 * twelve would be counting something nobody can see.
 */
const BOT_SETTINGS_SECTIONS = BOTS_SECTIONS.map((s) => s.title);

/**
 * The dropzone's promise, and it is the SERVER's number: `maxSnapshotBodyBytes`
 * in internal/admin/api/handler/snapshot.go, 32 MiB.
 *
 * It is sized off the bot roster, which is by an order of magnitude the largest
 * thing an envelope carries — ten thousand rows is 2.7–4.5 MB of the file
 * before a single template, board or phrase. The browser's copy exists so a
 * file that overruns it is refused with a sentence rather than with a 413 the
 * operator has to interpret; a copy LOWER than the server's does the opposite
 * of that, refusing a file the platform would have accepted, on the receiving
 * side, after the operator has already downloaded it and committed to the move.
 * The mockup's «up to 5 MB» is superseded — see the deviations list.
 */
const MAX_UPLOAD_BYTES = 32 * 1024 * 1024;

/** The cap as the dropzone and its refusal say it. One constant, so the
 *  sentence and the check cannot drift apart the way they just did. */
const MAX_UPLOAD_LABEL = "32 MB";

/** The two activity actions the "Recent transfers" card is built from. */
const TRANSFER_ACTIONS = [
  "project.snapshot_exported",
  "project.snapshot_applied",
];

/** Boards are grouped in this order when the sub-line names their statuses. */
const BOARD_STATUS_ORDER: LeaderboardStatus[] = ["active", "paused", "archived"];

const HIST_DATE = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
});
const HIST_TIME = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "Aug 2, 13:58" — the history rows' timestamp (mockup L450). */
function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${HIST_DATE.format(d)}, ${HIST_TIME.format(d)}`;
}

/**
 * True when the project has never been configured — `games.config` is created
 * as `{}` and only ever grows keys, so an empty object is the marker. Such a
 * project still exports: what travels is the platform defaults (mockup L506).
 */
function isUnconfigured(config: unknown): boolean {
  if (!config || typeof config !== "object" || Array.isArray(config)) return true;
  return Object.keys(config as Record<string, unknown>).length === 0;
}

/** "4 active, 1 archived" (mockup L344) — only the groups that exist. */
function boardStatusPhrase(boards: Leaderboard[]): string {
  return BOARD_STATUS_ORDER.map(
    (status) =>
      [status, boards.filter((b) => b.status === status).length] as const,
  )
    .filter(([, n]) => n > 0)
    .map(([status, n]) => `${n} ${status}`)
    .join(", ");
}

/** The parsed file plus the plan the server computed for it. */
interface LoadedSnapshot {
  snapshot: Snapshot;
  plan: SnapshotPlan;
  fileName: string;
}

// === Include row ====================================================== //

interface IncludeRowProps {
  name: string;
  apiName: string;
  /** The live sub-line, or a Skeleton while the numbers load. */
  sub: ReactNode;
  /** The count badge, or a Skeleton while the numbers load. */
  count: ReactNode;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
  /** Dims the row — a section with nothing to carry over (mockup L510). */
  dimmed: boolean;
}

/** One "what goes into the file" row (mockup L329-L337). */
function IncludeRow({
  name,
  apiName,
  sub,
  count,
  checked,
  disabled,
  onChange,
  dimmed,
}: IncludeRowProps) {
  return (
    <div className={dimmed ? `${styles.row} ${styles.rowEmpty}` : styles.row}>
      <Checkbox
        className={styles.rowBox}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-label={name}
      />
      <div>
        <div className={styles.rowName}>{name}</div>
        <div className={styles.rowApi}>{apiName}</div>
        <div className={styles.rowSub}>{sub}</div>
      </div>
      <div className={styles.rowCount}>{count}</div>
    </div>
  );
}

/**
 * Why a dropped file was not planned, and how the screen should say so.
 *
 * A shape rather than a bare string because the two answers differ in kind. A
 * file that cannot be read at all is an error about THIS file, and the screen
 * swaps the dropzone for «Replace file»: the operator picks another one. A file
 * of the previous format is not broken — it is a snapshot of a project, taken
 * before the bot settings left `games.config` — and the answer is not another
 * file from the same folder but another EXPORT from the environment it came
 * from. The dropzone stays under it, because that is where the new file lands.
 */
interface ImportRefusal {
  variant: "danger" | "warn";
  title: string;
  message: string;
  /** Per-problem detail the server sent — the "see the list below" the copy promises. */
  problems: string[];
  /** Keep the dropzone below the alert instead of the «Replace file» button. */
  keepDropzone: boolean;
}

/** Everything that is this file's own fault. */
const GENERIC_REFUSAL = {
  variant: "danger",
  title: "This file can't be imported",
  problems: [] as string[],
  keepDropzone: false,
} as const;

/**
 * A snapshot taken before the bot settings had a table of their own.
 *
 * The server refuses it with the same `snapshot_unknown_schema` it refuses any
 * foreign format, and would be right to: the file carries the whole simulation
 * inside `config.bots`, a key that does not exist here any more, and accepting
 * it «as far as it goes» would write it into the tenant blob as a key nothing
 * reads. What the operator needs to hear is not that, though — it is which
 * version they are holding and that a fresh export of the same project carries
 * the settings properly.
 */
const legacySchemaRefusal: ImportRefusal = {
  variant: "warn",
  title: "This file is a snapshot of an older format",
  message:
    `It carries ${schemaName(LEGACY_SNAPSHOT_SCHEMA)}, where the bot settings ` +
    "still travelled inside games.config. Export the project again from the " +
    "source environment — the new file carries them as their own section.",
  problems: [],
  keepDropzone: true,
};

/**
 * A snapshot whose bot settings were taken before profiles had a play rhythm.
 *
 * Only the bot settings are out of date — the same file without them imports —
 * but the server refuses it with the bare code it gives any foreign format, so
 * the reason and the way out are said here.
 */
const preRhythmSchemaRefusal: ImportRefusal = {
  variant: "warn",
  title: "This file's bot settings are of an older format",
  message:
    `It carries ${schemaName(PRE_RHYTHM_SNAPSHOT_SCHEMA)}, where bot profiles ` +
    "have no play rhythm yet. Export the project again to get " +
    `${schemaName(SNAPSHOT_SCHEMA)}.`,
  problems: [],
  keepDropzone: true,
};

/**
 * A schema id as the screen says it: `project-snapshot@3`, without the
 * namespace. The `social-hub/` half is what tells one PLATFORM's files from
 * another's, and there is only one platform in this panel — printing it makes
 * an operator read past four words to reach the number that matters.
 */
function schemaName(schema: string): string {
  return schema.split("/").pop() ?? schema;
}

/**
 * A heading over a run of rows: a caption, a rule, and the tables the group is
 * about.
 *
 * There is deliberately NO master checkbox. The group is a way to read the
 * list, not a second state to keep in step with the rows under it — a tick that
 * meant «all four» would have a third value the moment somebody unticked one of
 * them, and every row here is still a decision of its own.
 */
function GroupHead({ title, apiName }: { title: string; apiName: string }) {
  return (
    <div className={styles.groupHead}>
      <span className={styles.groupTitle}>{title}</span>
      <span className={styles.groupRule} aria-hidden="true" />
      <span className={styles.groupApi}>{apiName}</span>
    </div>
  );
}

/** Placeholders that keep a row's height while its numbers are in flight. */
const SUB_SKELETON = <Skeleton width="70%" height={11} />;
const COUNT_SKELETON = (
  <Skeleton width={82} height={22} radius="var(--r-full)" />
);

// === Recent transfers ================================================= //

/** One history row's copy, built from an activity entry (mockup L446-L466). */
function describeTransfer(item: ActivityItem): {
  incoming: boolean;
  title: string;
  meta: string;
} {
  const incoming = item.action === "project.snapshot_applied";
  const changes = numericDetail(item, "changes");
  const title = incoming
    ? `Snapshot applied${
        changes === undefined
          ? ""
          : ` · ${plural(changes, "change", "changes")}`
      }`
    : "Snapshot exported";
  const sections = Array.isArray(item.details?.sections)
    ? (item.details.sections as unknown[]).filter(
        (s): s is string => typeof s === "string",
      )
    : [];
  const meta = [
    formatWhen(item.created_at),
    item.actor.email,
    sections.length > 0 ? sections.join(", ") : null,
  ]
    .filter((part): part is string => !!part)
    .join(" · ");
  return { incoming, title, meta };
}

// === Page ============================================================= //

export function TransferPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { user } = useAuth();
  // Export is open to every project member; import rewrites live settings and
  // can delete, so it is super_admin only (spec §8.2).
  const isSuperAdmin = user?.role === "super_admin";
  const { push } = useToast();
  const qc = useQueryClient();

  const projectQ = useProject(id);
  const project = projectQ.data;

  // Boards of every status: an archived board travels too (spec §5.2).
  const boardsQ = useQuery({
    queryKey: lbKeys.list(id, {}),
    queryFn: () => listLeaderboards(id, {}),
    enabled: !!id,
  });
  const boards = boardsQ.data;

  const phrasesQ = useQuery({
    queryKey: ["project", id, "transfer", "phrase-counts"],
    queryFn: () => listPhrases(id),
    enabled: !!id,
  });

  // The simulation's own document — read for one question only: is there
  // anything in it. A project that never configured its bots has no row, and
  // mirroring an empty document onto another project would not carry nothing
  // over, it would reset every number that project has.
  const botSettingsQ = useQuery({
    queryKey: botSettingsQueryKey(id),
    queryFn: () => getBotSettings(id),
    enabled: !!id,
  });

  // The two bot-source tables live outside games.config, exactly like the
  // phrase library, so each needs its own count for its own row.
  const rosterQ = useQuery({
    queryKey: ["project", id, "transfer", "roster-counts"],
    queryFn: () => getRosterCounts(id),
    enabled: !!id,
  });
  const templatesQ = useQuery({
    queryKey: ["project", id, "transfer", "guild-template-counts"],
    queryFn: () => getGuildTemplateCounts(id),
    enabled: !!id,
  });

  // One /rewards call per board: the list endpoint carries no reward count and
  // the badge promises one. Bounded by the number of boards a project has —
  // the leaderboards list renders them all unpaginated for the same reason —
  // and the cache is shared with the leaderboard detail pages.
  const rewardQs = useQueries({
    queries: (boards ?? []).map((b) => ({
      queryKey: lbKeys.rewards(id, b.id),
      queryFn: () => listRewards(id, b.id),
      enabled: !!id,
    })),
  });
  const rewardsTotal = rewardQs.every((q) => q.isSuccess)
    ? rewardQs.reduce((n, q) => n + (q.data?.length ?? 0), 0)
    : null;

  const activityQ = useQuery({
    queryKey: ["project", id, "activity", TRANSFER_ACTIONS],
    queryFn: () => fetchActivity(id, null, 5, TRANSFER_ACTIONS),
    enabled: !!id,
  });

  // What the human ticked. A section that turns out to be empty is dropped
  // from the envelope regardless (see `sections` below), so this never needs
  // reconciling against the data as it arrives.
  const [picked, setPicked] = useState({
    config: true,
    bot_settings: true,
    leaderboards: true,
    phrases: true,
    roster: true,
    guild_templates: true,
  });
  const [downloading, setDownloading] = useState(false);

  const [dragOver, setDragOver] = useState(false);
  /** Name of the file being parsed and planned right now; null when idle. */
  const [busyName, setBusyName] = useState<string | null>(null);
  const [importError, setImportError] = useState<ImportRefusal | null>(null);
  const [loaded, setLoaded] = useState<LoadedSnapshot | null>(null);
  // Lives at page level, not inside the dropzone: `Replace file` is also
  // reachable from the review screen, which unmounts the export card.
  const fileRef = useRef<HTMLInputElement>(null);
  const infoRef = useRef<HTMLButtonElement>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  // --- Derived counts ------------------------------------------------- //

  // `groups` counts every phrase, paused ones included — the size of what the
  // file would carry. `coverage` counts live lines only, so it cannot stand in
  // for it: a library that is entirely paused would read as empty, the section
  // would disable itself, and the export would silently carry no phrases.
  const coverage = phrasesQ.data?.groups;
  const phraseTotal = coverage
    ? coverage.reduce((n, c) => n + c.count, 0)
    : null;
  const phraseCategories = coverage
    ? new Set(coverage.filter((c) => c.count > 0).map((c) => c.category)).size
    : 0;
  // Configured order (default locale first), narrowed to the ones that
  // actually carry phrases — "locales ru, en, de" (mockup L354).
  const phraseLocales = (() => {
    if (!coverage) return [];
    const inUse = new Set(
      coverage.filter((c) => c.count > 0).map((c) => c.locale),
    );
    const ordered = (phrasesQ.data?.locales ?? []).filter((l) => inUse.has(l));
    for (const locale of inUse) {
      if (!ordered.includes(locale)) ordered.push(locale);
    }
    return ordered;
  })();

  // Both bot-source rows count the WHOLE table, spent rows included: those
  // travel too, minus their spend link, which is what makes a roster carried
  // dev → prod arrive whole.
  const roster = rosterQ.data;
  const templates = templatesQ.data;

  const botSettings = botSettingsQ.data?.config as
    | Record<string, unknown>
    | undefined;
  const botSettingsEmpty =
    botSettings !== undefined && Object.keys(botSettings).length === 0;

  const boardsEmpty = boards?.length === 0;
  const phrasesEmpty = phraseTotal === 0;
  const rosterEmpty = roster?.total === 0;
  const templatesEmpty = templates?.total === 0;

  /**
   * What the download actually carries. An empty section is omitted whatever
   * the stored tick says, which is also why an empty row can render unticked
   * and disabled without an effect reconciling state against the data.
   */
  const sections: SnapshotSections = {
    config: picked.config,
    bot_settings: picked.bot_settings && !botSettingsEmpty,
    leaderboards: picked.leaderboards && !boardsEmpty,
    phrases: picked.phrases && !phrasesEmpty,
    roster: picked.roster && !rosterEmpty,
    guild_templates: picked.guild_templates && !templatesEmpty,
  };
  const nothingSelected =
    !sections.config &&
    !sections.bot_settings &&
    !sections.leaderboards &&
    !sections.phrases &&
    !sections.roster &&
    !sections.guild_templates;

  const filename = project ? snapshotFilename(project.name) : null;

  // --- Export --------------------------------------------------------- //

  async function onDownload() {
    setDownloading(true);
    try {
      await downloadSnapshot(id, sections, filename ?? "snapshot.json");
      // The export is an activity entry of its own — refresh the history card.
      void qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
    } catch (err) {
      push({
        variant: "danger",
        title: "Couldn't download the snapshot",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    } finally {
      setDownloading(false);
    }
  }

  // --- Import --------------------------------------------------------- //

  function readText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error ?? new Error("read failed"));
      reader.onload = () => resolve(String(reader.result ?? ""));
      reader.readAsText(file);
    });
  }

  async function handleFile(file: File | null | undefined) {
    if (!file) return;
    setImportError(null);
    if (file.size > MAX_UPLOAD_BYTES) {
      setImportError({
        ...GENERIC_REFUSAL,
        message: `This file is larger than ${MAX_UPLOAD_LABEL}, which is more than the API will read. A project snapshot is never that big — check you picked the right one.`,
      });
      return;
    }
    setBusyName(file.name);
    try {
      let text: string;
      try {
        text = await readText(file);
      } catch {
        setImportError({
          ...GENERIC_REFUSAL,
          message: "Couldn't read this file. Try picking it again.",
        });
        return;
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        // Same treatment as the server's own rejection of unparseable JSON.
        setImportError({
          ...GENERIC_REFUSAL,
          message: describeError("snapshot_bad_json"),
        });
        return;
      }
      const snapshot = parsed as Snapshot;
      // The previous format is recognised from the file rather than from the
      // refusal, because only the file says WHICH older format it is. The
      // server refuses it either way — this is what turns «this isn't a
      // project snapshot» into a sentence naming the version and what to do
      // about it.
      if (snapshot?.schema === LEGACY_SNAPSHOT_SCHEMA) {
        setImportError(legacySchemaRefusal);
        return;
      }
      if (
        snapshot?.schema === PRE_RHYTHM_SNAPSHOT_SCHEMA &&
        Object.prototype.hasOwnProperty.call(snapshot, "bot_settings")
      ) {
        setImportError(preRhythmSchemaRefusal);
        return;
      }
      const plan = await planSnapshot(id, snapshot);
      setLoaded({ snapshot, plan, fileName: file.name });
    } catch (err) {
      setImportError(
        err instanceof ApiError
          ? {
              ...GENERIC_REFUSAL,
              message: describeError(err.code),
              problems: errorProblems(err),
            }
          : { ...GENERIC_REFUSAL, message: describeError("internal") },
      );
    } finally {
      setBusyName(null);
    }
  }

  function openFilePicker() {
    setImportError(null);
    fileRef.current?.click();
  }

  function onDrop(e: ReactDragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (busyName) return;
    void handleFile(e.dataTransfer?.files?.[0]);
  }

  function cancelImport() {
    setLoaded(null);
    setImportError(null);
  }

  function replaceFile() {
    setLoaded(null);
    openFilePicker();
  }

  function handleApplied(appliedChanges: number, scheduledBoards: number) {
    setLoaded(null);
    setImportError(null);
    push({
      variant: "success",
      // A scheduled board is inside `appliedChanges` — its package is as
      // durable as any other row the transaction wrote — but it has not
      // reached a player yet, and a toast that stopped at "applied" would be
      // the one place on this journey that said otherwise.
      message: `Snapshot applied · ${plural(appliedChanges, "change", "changes")}${
        scheduledBoards > 0
          ? scheduledBoards === 1
            ? " · 1 board waits for its next cut"
            : ` · ${scheduledBoards} boards wait for their next cut`
          : ""
      }`,
    });
    // Everything an apply could have touched, plus the activity feed.
    void qc.invalidateQueries({ queryKey: projectQueryKey(id) });
    void qc.invalidateQueries({ queryKey: lbKeys.listAll(id) });
    void qc.invalidateQueries({ queryKey: ["leaderboard.rewards", id] });
  }

  // --- Header --------------------------------------------------------- //

  const reviewing = loaded !== null;
  const trail = [
    { label: "All projects", to: "/projects" },
    { label: project?.name ?? "Project", to: `/projects/${id}/overview` },
  ];
  const breadcrumb = reviewing
    ? [
        ...trail,
        { label: "Transfer", to: `/projects/${id}/transfer` },
        { label: "Import" },
      ]
    : [...trail, { label: "Transfer" }];

  const headerActions = reviewing ? (
    <Button variant="ghost" size="sm" onClick={cancelImport}>
      Cancel import
    </Button>
  ) : (
    <>
      <Button
        ref={infoRef}
        variant="ghost"
        size="sm"
        icon={<Info size={13} />}
        aria-expanded={infoOpen}
        onClick={() => setInfoOpen((open) => !open)}
      >
        What&apos;s included
      </Button>
      <Popover
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        anchorRef={infoRef}
        placement="bottom-end"
        width={340}
      >
        <div className={styles.infoPop}>
          <div className={styles.infoTitle}>Travels in the file</div>
          <ul className={styles.infoList}>
            <li>
              The project configuration — every section of{" "}
              <code>games.config</code>: {CONFIG_SECTIONS.join(", ")}
            </li>
            <li>Leaderboard definitions and their rewards</li>
            {/* The simulation is one item and not four scattered through the
                list. It used to be a clause on the config line — «and the bot
                settings edited on the Bots page» — because that is where the
                settings were stored; they are their own document now, and the
                sentence can say what the file actually carries. */}
            <li>
              The bot simulation — its settings, the phrase library with each
              phrase&apos;s status, and the roster and guild templates, every
              row, spent ones included; each one arrives unspent
            </li>
          </ul>
          <div className={styles.infoTitle}>Stays behind</div>
          <ul className={styles.infoList}>
            <li>
              <code>auth.facebook.app_secret</code> — the receiving environment
              keeps its own
            </li>
            <li>The project name, its API key and its team</li>
            <li>Players, guilds, chat, scores, cycles and granted rewards</li>
          </ul>
        </div>
      </Popover>
    </>
  );

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={reviewing ? "Review import" : "Transfer"}
        actions={headerActions}
      />

      {/* Kept outside both screens so `Replace file` works from either. */}
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        className={styles.fileInput}
        aria-label="Snapshot file"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset so picking the same file twice fires change again.
          e.target.value = "";
          void handleFile(file);
        }}
      />

      <div className={loaded ? `${styles.page} ${styles.pageWithBar}` : styles.page}>
        {loaded ? (
          <ImportReview
            projectId={id}
            projectName={project?.name ?? ""}
            snapshot={loaded.snapshot}
            plan={loaded.plan}
            fileName={loaded.fileName}
            onCancel={cancelImport}
            onReplaceFile={replaceFile}
            onApplied={handleApplied}
          />
        ) : (
          <div className={styles.grid}>
            {/* === Left column ======================================== */}
            <div className={styles.column}>
              {/* --- Export snapshot (mockup L318-L379) --------------- */}
              <Card>
                <CardHeader>
                  <h3 className={styles.cardTitle}>
                    <span className={styles.titleIcon} aria-hidden="true">
                      <Download size={16} />
                    </span>
                    Export snapshot
                  </h3>
                  <span className={styles.apiName}>
                    games.config · leaderboards · bot_settings · bot_phrases ·
                    bot_roster · bot_guild_templates
                  </span>
                </CardHeader>
                <CardBody>
                  <p className={styles.desc}>
                    Collects this project&apos;s settings into a single file.
                    Nothing changes on this environment — the file is what
                    carries these settings to another one.
                  </p>

                  <GroupHead
                    title="Project"
                    apiName="games.config · leaderboards"
                  />

                  <IncludeRow
                    name="Project configuration"
                    apiName="games.config"
                    dimmed={false}
                    disabled={false}
                    checked={sections.config}
                    onChange={(next) =>
                      setPicked((p) => ({ ...p, config: next }))
                    }
                    sub={
                      projectQ.isLoading
                        ? SUB_SKELETON
                        : projectQ.isError || !project
                          ? "Couldn't read this project's configuration — the export itself still works."
                          : isUnconfigured(project.config)
                            ? "Platform defaults — this project has never been configured."
                            : CONFIG_SECTIONS.join(" · ")
                    }
                    count={
                      <Badge variant="neutral">
                        {plural(CONFIG_SECTIONS.length, "section", "sections")}
                      </Badge>
                    }
                  />

                  <IncludeRow
                    name="Leaderboards & rewards"
                    apiName="leaderboards + leaderboard_rewards"
                    dimmed={boardsEmpty}
                    disabled={boardsEmpty}
                    checked={sections.leaderboards}
                    onChange={(next) =>
                      setPicked((p) => ({ ...p, leaderboards: next }))
                    }
                    sub={
                      boardsQ.isLoading
                        ? SUB_SKELETON
                        : boardsQ.isError || !boards
                          ? "Couldn't count this project's leaderboards — the export itself still works."
                          : boards.length === 0
                            ? "No leaderboards yet — nothing to carry over."
                            : `${boardStatusPhrase(boards)}. Board definitions and their rewards — no cycles, no scores, no granted prizes.`
                    }
                    count={
                      boardsQ.isLoading ? (
                        COUNT_SKELETON
                      ) : boardsQ.isError || !boards ? (
                        <Badge variant="neutral">—</Badge>
                      ) : boards.length === 0 ? (
                        <Badge variant="neutral">empty</Badge>
                      ) : (
                        <Badge variant="neutral">
                          {plural(boards.length, "board", "boards")}
                          {rewardsTotal !== null &&
                            ` · ${plural(rewardsTotal, "reward", "rewards")}`}
                        </Badge>
                      )
                    }
                  />

                  <GroupHead
                    title="Bots"
                    apiName="bot_settings · bot_phrases · bot_roster · bot_guild_templates"
                  />

                  <IncludeRow
                    name="Bot settings"
                    apiName="bot_settings"
                    dimmed={botSettingsEmpty}
                    disabled={botSettingsEmpty}
                    checked={sections.bot_settings}
                    onChange={(next) =>
                      setPicked((p) => ({ ...p, bot_settings: next }))
                    }
                    sub={
                      botSettingsQ.isLoading
                        ? SUB_SKELETON
                        : botSettingsQ.isError || botSettings === undefined
                          ? "Couldn't read this project's bot settings — the export itself still works."
                          : botSettingsEmpty
                            ? "Platform defaults — this project has never configured its simulation."
                            : `${BOT_SETTINGS_SECTIONS.join(" · ")} — plus the source switch and the name pools, both edited on the Source tab.`
                    }
                    count={
                      botSettingsQ.isLoading ? (
                        COUNT_SKELETON
                      ) : botSettingsQ.isError || botSettings === undefined ? (
                        <Badge variant="neutral">—</Badge>
                      ) : botSettingsEmpty ? (
                        <Badge variant="neutral">empty</Badge>
                      ) : (
                        <Badge variant="neutral">
                          {plural(
                            BOT_SETTINGS_SECTIONS.length,
                            "section",
                            "sections",
                          )}
                        </Badge>
                      )
                    }
                  />

                  <IncludeRow
                    name="Bot phrases"
                    apiName="bot_phrases"
                    dimmed={phrasesEmpty}
                    disabled={phrasesEmpty}
                    checked={sections.phrases}
                    onChange={(next) =>
                      setPicked((p) => ({ ...p, phrases: next }))
                    }
                    sub={
                      phrasesQ.isLoading
                        ? SUB_SKELETON
                        : phrasesQ.isError || phraseTotal === null
                          ? "Couldn't count this project's bot phrases — the export itself still works."
                          : phraseTotal === 0
                            ? "The phrase library is empty — bots fall back to the platform list."
                            : `${plural(phraseCategories, "category", "categories")} · ${
                                phraseLocales.length === 1 ? "locale" : "locales"
                              } ${phraseLocales.join(", ")}. Status (live / paused) travels with the text.`
                    }
                    count={
                      phrasesQ.isLoading ? (
                        COUNT_SKELETON
                      ) : phrasesQ.isError || phraseTotal === null ? (
                        <Badge variant="neutral">—</Badge>
                      ) : phraseTotal === 0 ? (
                        <Badge variant="neutral">empty</Badge>
                      ) : (
                        <Badge variant="neutral">
                          {plural(phraseTotal, "phrase", "phrases")}
                        </Badge>
                      )
                    }
                  />

                  <IncludeRow
                    name="Bot roster"
                    apiName="bot_roster"
                    dimmed={!!rosterEmpty}
                    disabled={!!rosterEmpty}
                    checked={sections.roster}
                    onChange={(next) =>
                      setPicked((p) => ({ ...p, roster: next }))
                    }
                    sub={
                      rosterQ.isLoading
                        ? SUB_SKELETON
                        : rosterQ.isError || !roster
                          ? "Couldn't count this project's bot roster — the export itself still works."
                          : roster.total === 0
                            ? "No roster rows yet — nothing to carry over."
                            : `${plural(roster.total, "row", "rows")}, ${roster.spent} of them already spent here. Rows travel; the bots they became stay in this project — a bot is a player, and players are not settings.`
                    }
                    count={
                      rosterQ.isLoading ? (
                        COUNT_SKELETON
                      ) : rosterQ.isError || !roster ? (
                        <Badge variant="neutral">—</Badge>
                      ) : roster.total === 0 ? (
                        <Badge variant="neutral">empty</Badge>
                      ) : (
                        <Badge variant="neutral">
                          {plural(roster.total, "row", "rows")}
                        </Badge>
                      )
                    }
                  />

                  <IncludeRow
                    name="Guild templates"
                    apiName="bot_guild_templates"
                    dimmed={!!templatesEmpty}
                    disabled={!!templatesEmpty}
                    checked={sections.guild_templates}
                    onChange={(next) =>
                      setPicked((p) => ({ ...p, guild_templates: next }))
                    }
                    sub={
                      templatesQ.isLoading
                        ? SUB_SKELETON
                        : templatesQ.isError || !templates
                          ? "Couldn't count this project's guild templates — the export itself still works."
                          : templates.total === 0
                            ? "No templates yet — nothing to carry over."
                            : `${plural(templates.total, "template", "templates")}, ${templates.spent} of them already created here. Templates travel; the guilds they became stay.`
                    }
                    count={
                      templatesQ.isLoading ? (
                        COUNT_SKELETON
                      ) : templatesQ.isError || !templates ? (
                        <Badge variant="neutral">—</Badge>
                      ) : templates.total === 0 ? (
                        <Badge variant="neutral">empty</Badge>
                      ) : (
                        <Badge variant="neutral">
                          {plural(templates.total, "template", "templates")}
                        </Badge>
                      )
                    }
                  />

                  <AlertStack className={styles.warnWrap}>
                    <Alert
                      variant="warn"
                      icon={<Lock size={14} />}
                      title="Secrets stay out of the file"
                    >
                      <code>auth.facebook.app_secret</code> is stripped — the
                      receiving environment keeps its own. <code>app_id</code>{" "}
                      travels. The project API key is never part of a snapshot.
                    </Alert>
                    <Alert variant="info" title="Every row arrives unspent">
                      A row&apos;s link to the bot or the guild it became is
                      local to the project that spent it. On the receiving side
                      every row is available again — which is the point of
                      carrying a roster from dev to prod.
                    </Alert>
                  </AlertStack>
                </CardBody>
                <CardFooter className={styles.exportFooter}>
                  <span className={styles.filename}>
                    <FileIcon size={13} aria-hidden="true" />
                    {filename ? (
                      <span className={styles.filenameText}>{filename}</span>
                    ) : (
                      <Skeleton width={210} height={12} />
                    )}
                  </span>
                  <Button
                    variant="primary"
                    icon={<Download size={14} />}
                    loading={downloading}
                    disabled={nothingSelected}
                    onClick={() => void onDownload()}
                  >
                    Download snapshot
                  </Button>
                </CardFooter>
              </Card>

              {/* --- Import snapshot (mockup L382-L400) --------------- */}
              {isSuperAdmin ? (
                <Card>
                  <CardHeader>
                    <h3 className={styles.cardTitle}>
                      <span className={styles.titleIcon} aria-hidden="true">
                        <Upload size={16} />
                      </span>
                      Import snapshot
                    </h3>
                    <Badge variant="accent">super_admin</Badge>
                  </CardHeader>
                  <CardBody>
                    {importError && (
                      <div className={styles.importRefusal}>
                        <Alert
                          variant={importError.variant}
                          title={importError.title}
                          icon={<AlertTriangle size={14} />}
                        >
                          {importError.message}
                          {importError.problems.length > 0 && (
                            <ul className={styles.importProblems}>
                              {importError.problems.map((p) => (
                                <li key={p}>{p}</li>
                              ))}
                            </ul>
                          )}
                        </Alert>
                        {!importError.keepDropzone && (
                          <div className={styles.importErrorActions}>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={openFilePicker}
                            >
                              Replace file
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    {(!importError || importError.keepDropzone) && (
                      <div
                        className={
                          dragOver
                            ? `${styles.dropzone} ${styles.dropzoneOver}`
                            : styles.dropzone
                        }
                        onDragOver={(e) => {
                          e.preventDefault();
                          if (!busyName) setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={onDrop}
                      >
                        <span className={styles.dzIcon} aria-hidden="true">
                          <Upload size={20} />
                        </span>
                        <span className={styles.dzTitle}>
                          {busyName
                            ? `Checking ${busyName}…`
                            : "Drop a snapshot file here"}
                        </span>
                        <span className={styles.dzOr}>or</span>
                        <Button
                          variant="secondary"
                          size="sm"
                          loading={!!busyName}
                          onClick={openFilePicker}
                        >
                          Choose file
                        </Button>
                        <span className={styles.dzHint}>
                          .json · up to {MAX_UPLOAD_LABEL} · schema{" "}
                          {schemaName(SNAPSHOT_SCHEMA)}
                        </span>
                      </div>
                    )}
                    <p className={`${styles.desc} ${styles.importDesc}`}>
                      The file is parsed and compared against this project first
                      — you get an itemised list of changes and confirm it
                      separately. Nothing is applied until you do.
                    </p>
                  </CardBody>
                </Card>
              ) : (
                <p className={styles.memberNote}>
                  <span className={styles.memberNoteIcon} aria-hidden="true">
                    <Lock size={14} />
                  </span>
                  <span>
                    Importing a snapshot is available to super admins only — it
                    overwrites live settings. Exporting one is open to everyone
                    on this project.
                  </span>
                </p>
              )}
            </div>

            {/* === Right column ====================================== */}
            <div className={styles.column}>
              {/* --- How it works (mockup L406-L440) ----------------- */}
              <Card>
                <CardHeader>
                  <h3 className={styles.cardTitle}>How it works</h3>
                </CardHeader>
                <CardBody>
                  <div className={styles.steps}>
                    {[
                      {
                        title: "Tune it on dev",
                        sub: "Config, leaderboards, phrases and the bot source — the usual way, through this admin.",
                      },
                      {
                        title: "Download the snapshot",
                        sub: "One file. Keep it in git next to the release, or hand it to a teammate.",
                      },
                      {
                        title: "Upload it on prod",
                        sub: "The same screen there shows exactly what would change.",
                      },
                      {
                        title: "Apply what you picked",
                        sub: "Sections have checkboxes — take the config and leave the leaderboards alone.",
                      },
                    ].map((step, i) => (
                      <div className={styles.step} key={step.title}>
                        <span className={styles.stepNum} aria-hidden="true">
                          {i + 1}
                        </span>
                        <div>
                          <div className={styles.stepTitle}>{step.title}</div>
                          <div className={styles.stepSub}>{step.sub}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>

              {/* --- Recent transfers (mockup L442-L472) ------------- */}
              <Card>
                <CardHeader>
                  <h3 className={styles.cardTitle}>Recent transfers</h3>
                </CardHeader>
                <CardBody>
                  {activityQ.isLoading ? (
                    <div className={styles.hist} aria-busy="true">
                      {[0, 1, 2].map((i) => (
                        <div className={styles.histRow} key={i}>
                          <Skeleton
                            width={24}
                            height={24}
                            radius="var(--r-sm)"
                          />
                          <div>
                            <Skeleton width="70%" height={12} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : activityQ.isError || !activityQ.data ? (
                    <Alert
                      variant="danger"
                      title="Couldn't load recent transfers"
                    >
                      {activityQ.error instanceof ApiError
                        ? describeError(activityQ.error.code)
                        : describeError("internal")}
                    </Alert>
                  ) : activityQ.data.items.length === 0 ? (
                    <p className={styles.histEmpty}>
                      No transfers yet. Exports and imports show up here.
                    </p>
                  ) : (
                    <ul className={styles.hist}>
                      {activityQ.data.items.map((item) => {
                        const { incoming, title, meta } =
                          describeTransfer(item);
                        return (
                          <li className={styles.histRow} key={item.id}>
                            <span
                              className={`${styles.histIcon} ${
                                incoming ? styles.histIn : styles.histOut
                              }`}
                              aria-hidden="true"
                            >
                              {incoming ? (
                                <ArrowDown size={13} />
                              ) : (
                                <ArrowUp size={13} />
                              )}
                            </span>
                            <div>
                              <div className={styles.histTitle}>{title}</div>
                              <div className={styles.histSub}>{meta}</div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </CardBody>
                <CardFooter className={styles.histFooter}>
                  <Link
                    className={styles.footerLink}
                    to={`/projects/${id}/overview`}
                  >
                    Full activity log →
                  </Link>
                </CardFooter>
              </Card>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
